// @file Vuex Store module for all things related to posts or posting on a padlet.
/* eslint-disable @typescript-eslint/camelcase, no-param-reassign */
import { JsonAPIResponse, Wish } from '@padlet/arvo'
import appCan from 'bits/app_can'
import { getDisplayAttributes } from 'bits/beethoven'
import { captureFetchException } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import buildPost from 'surface/build_post'
import PadletApi from 'surface/padlet_api'
import {
  recomputeTimelineSortIndex,
  // Commented out because we deprecated backchannel On June 30 2021
  // so we don't need to use this function to sort backchannel posts anymore
  // sortPostsByDate,
  sortPostsByPostOrderArray,
  sortPostsBySortIndex,
} from 'surface/sorter'
import { AttachmentProps, Cid, Id, Post, PostAttributes, UpdatePostArgs } from 'types'
import Vue from 'vue'
import { ActionContext, Module } from 'vuex'
import {
  addCidIfAbsent,
  customSavePost,
  CustomSavePostRequest,
  deleteAllPosts,
  deletePost,
  isEmpty,
  isNew,
  isNonEmpty,
  PostConfiguration,
  reconcilePostsAfterFetch,
  savePost,
} from 'vuexstore/helpers/post'
import { isNew as isSectionNew, Section } from 'vuexstore/helpers/section'
import { RootState } from 'vuexstore/surface/types'

interface PostState {
  chatAttachment: string | null
  newestPostCid: Cid | null
  pendingPostEntitiesByCid: Record<Cid, Post>
  postBeingEditedCid: Cid | null
  postEntitiesByCid: Record<Cid, Post>
  isInitialPaginatedDataLoadDone: boolean
  postsMarkedAsDeleted: Post[]
  postOrder: Cid[]
  mostRecentlyTouchedSectionId: number | null
  mostRecentlyChangedGeolocationPostCid: Cid | null
}

const defaultState = (): PostState => ({
  chatAttachment: null,
  newestPostCid: null,
  pendingPostEntitiesByCid: {},
  postBeingEditedCid: null,
  postEntitiesByCid: {},
  isInitialPaginatedDataLoadDone: false,
  postsMarkedAsDeleted: [],
  postOrder: [],
  mostRecentlyTouchedSectionId: null,
  mostRecentlyChangedGeolocationPostCid: null,
})

const postModule: Module<PostState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    // Posts-related collections
    postEntitiesByCid: (state) => state.postEntitiesByCid,
    postCids: (state): Cid[] => Object.keys(state.postEntitiesByCid),
    postsArray: (state): Post[] => Object.values(state.postEntitiesByCid),
    nonEmptyPostsArray: (state, getters): Post[] => getters.postsArray.filter(isNonEmpty),
    postEntitiesById: (state, getters) =>
      getters.postsArray.reduce((hash, post) => {
        if (post.id) {
          hash[post.id] = post
        }
        return hash
      }, {}),

    // Pending posts-related collections
    pendingPostEntitiesByCid: (state) => state.pendingPostEntitiesByCid,
    pendingPostCids: (state): Cid[] => Object.keys(state.pendingPostEntitiesByCid),
    pendingPosts: (state): Post[] => Object.values(state.pendingPostEntitiesByCid),

    // Cached post getter methods
    getPostByCid:
      (state): Function =>
      (cid: Cid): Post | null =>
        state.postEntitiesByCid[cid],
    getPostByServerId:
      (_, getters): Function =>
      (id: Id): Post | null =>
        getters.postEntitiesById[id],
    getPost:
      (_, getters): Function =>
      ({ id, cid }: { id: Id; cid: Cid }): Post | null =>
        getters.getPostByCid(cid) || getters.getPostByServerId(id),
    getLiveOrDeletedPost:
      (_, getters): Function =>
      ({ id, cid }: { id: Id; cid: Cid }): Post | null =>
        getters.getPost({ id, cid }) || getters.findDeletedPost({ cid, id }),
    getAttachmentPropsFromPost:
      (_, getters): Function =>
      (cid: Cid): AttachmentProps | null =>
        getters.getPostByCid(cid)?.wish_content?.attachment_props ?? null,

    // Used by map format to pan to a post that has just changed geolocation, handled by a watcher in surface_container_map.vue
    mostRecentlyChangedGeolocationPostCid: (state): Cid | null => state.mostRecentlyChangedGeolocationPostCid,

    // Others
    postOrder: (state) => state.postOrder,
    postMaxAbsSortIndex: (state, getters): number =>
      Math.max(...getters.postsArray.map((post) => Math.abs(post.sort_index))),
    isInitialPaginatedDataLoadDone: (state) => state.isInitialPaginatedDataLoadDone,
    postBeingEditedCid: (state) => state.postBeingEditedCid,
    postBeingEdited: (state): Post | null => {
      return state.postBeingEditedCid ? state.postEntitiesByCid[state.postBeingEditedCid] : null
    },
    chatAttachment: (state) => state.chatAttachment,
    newestPostCid: (state) => state.newestPostCid,
    mostRecentlyTouchedSectionId: (state) => state.mostRecentlyTouchedSectionId,
    newestPost: (state, getters): Post => getters.getPostByCid(state.newestPostCid),
    postCidsBySection: (state, getters, _, rootGetters): Record<string, string> => {
      if (rootGetters.isShelf) {
        const postCidsBySection = {}
        getters.postCids.forEach((pid: string): void => {
          const p = state.postEntitiesByCid[pid]
          if (!p.wall_section_id || (rootGetters.isUsingNewComposerModal && isEmpty(p))) return
          postCidsBySection[p.wall_section_id] = postCidsBySection[p.wall_section_id] || []
          postCidsBySection[p.wall_section_id].push(pid)
        })
        return postCidsBySection
      }
      return {}
    },
    sortedPosts(_, getters, rootState, rootGetters): Post[] {
      const postsToShow = (
        rootState.inlinePostEdit && !rootGetters.isUsingNewComposerModal
          ? getters.postsArray
          : getters.nonEmptyPostsArray
      ).filter((post) => post.id || !rootGetters['draft/isCidBeingDrafted'](post.cid))

      if (rootGetters.isTimeline) {
        if (isAppUsing('wishOrderArray')) {
          const postOrder = getters.postOrder
          if (!postOrder || postOrder.length === 0) return []
          return sortPostsByPostOrderArray(postsToShow, postOrder).reverse()
        }
        return sortPostsBySortIndex(postsToShow).reverse()
      }
      if (isAppUsing('wishOrderArray')) {
        return sortPostsByPostOrderArray(postsToShow, getters.postOrder)
      }
      return sortPostsBySortIndex(postsToShow)
      // return sortPostsByPostOrderArray(postsToShow, getters.postOrder)
    },
    postConfiguration(state, getters, rootState, rootGetters): PostConfiguration {
      if (rootGetters.isTable) return PostConfiguration.BodyOrAttachment
      return PostConfiguration.All
    },
    canUserViewPost(state, getters, rootState, rootGetters): (post: PostAttributes) => boolean {
      return (post: PostAttributes): boolean => {
        return (
          post.published ||
          rootGetters.canIModerate ||
          (rootState.user.id !== null && post.author_id === rootState.user.id)
        )
      }
    },
    findDeletedPost(state): (post: PostAttributes) => Post | undefined {
      return (post: PostAttributes): Post | undefined => {
        return state.postsMarkedAsDeleted.find(
          // Post might have been deleted before they can be created,
          // so it's possible that p.id and post.id are both undefined.
          (p: PostAttributes) => p.cid === post.cid || (p.id && post.id && p.id === post.id),
        )
      }
    },
    isPostMarkedAsDeleted(state, getters): (post: PostAttributes) => boolean {
      return (post: PostAttributes): boolean => !!getters.findDeletedPost(post)
    },
    isPostAlive(state, getters): ({ post, postCid }: { post: PostAttributes; postCid: Cid }) => boolean {
      return ({ post, postCid }: { post: PostAttributes; postCid: Cid }): boolean => {
        post = post || getters.getPostByCid(postCid)
        if (!post) return false
        if (getters.isPostMarkedAsDeleted(post)) return false
        return true
      }
    },
  },
  mutations: {
    FETCH_POSTS_SUCCESS(state, { newPostEntitiesByCid, isInitialPaginatedDataLoadDone }): void {
      state.postEntitiesByCid = newPostEntitiesByCid || {}
      if (isInitialPaginatedDataLoadDone) state.isInitialPaginatedDataLoadDone = true
    },
    FETCH_POST_ORDER_SUCCESS(state, postOrder): void {
      state.postOrder = postOrder
    },
    // Updates pending post with the given attributes,
    // e.g. assign a table cell to dragged and dropped files before they are uploaded.
    UPDATE_PENDING_POST(state, postAttributes): void {
      const post = state.pendingPostEntitiesByCid[postAttributes.cid]
      if (!post) return
      const posts = {
        ...state.pendingPostEntitiesByCid,
        [post.cid]: { ...post, ...postAttributes },
      }
      state.pendingPostEntitiesByCid = posts
    },
    ADD_PENDING_POST(state, postAttributes): void {
      const post = addCidIfAbsent(postAttributes)
      Vue.set(state.pendingPostEntitiesByCid, post.cid, post)
    },
    REMOVE_PENDING_POST(state, { cid }): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      delete newPendingPostEntities[cid]
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REMOVE_PENDING_POSTS(state, { cids }): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      cids.forEach((cid): void => {
        delete newPendingPostEntities[cid]
      })
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REMOVE_ALL_PENDING_POSTS_WITHOUT_SECTION(state): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      const ppids = Object.keys(state.pendingPostEntitiesByCid)
      ppids.forEach((ppid): void => {
        const pendingPost = newPendingPostEntities[ppid]
        if (!pendingPost.wall_section_id) {
          delete newPendingPostEntities[ppid]
        }
      })
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REJECT_POSTS_WITH_FILE_SIZE_EXCEEDED(state, file): void {
      if (file) {
        const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
        const ppids = Object.keys(state.pendingPostEntitiesByCid)
        ppids.forEach((ppid): void => {
          const pendingPost = newPendingPostEntities[ppid]
          if (pendingPost.file === file) {
            delete newPendingPostEntities[ppid]
          }
        })
        state.pendingPostEntitiesByCid = newPendingPostEntities
      }
    },
    START_NEW_POST(state, postAttributes): void {
      const post = addCidIfAbsent(postAttributes)
      Vue.set(state.postEntitiesByCid, post.cid, post)
      state.postBeingEditedCid = post.cid
      if (postAttributes.wall_section_id) {
        state.mostRecentlyTouchedSectionId = postAttributes.wall_section_id
      }
    },
    CREATED_POST(state, postAttributes): void {
      const post = addCidIfAbsent(postAttributes)
      if (postAttributes.wall_section_id && postAttributes.wall_section_id !== state.mostRecentlyTouchedSectionId) {
        state.mostRecentlyTouchedSectionId = postAttributes.wall_section_id
      }
      Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    SAVE_POST_ID(state, { id, cid }): void {
      const post = state.postEntitiesByCid[cid]
      if (post) Vue.set(post, 'id', id)
    },
    CREATED_POST_ERROR(state, post): void {
      // if (post.cid) {
      //   Vue.delete(state.postEntitiesByCid, post.cid)
      // }
    },
    MARK_NEWEST_POST(state, cid): void {
      state.newestPostCid = cid
    },
    DELETED_POST(state, post: Post): void {
      if (state.postBeingEditedCid === post.cid) {
        state.postBeingEditedCid = null
      }
      state.postsMarkedAsDeleted.push(post)
      Vue.delete(state.postEntitiesByCid, post.cid)
      if (isAppUsing('wishOrderArray')) {
        const postIndex = state.postOrder.indexOf(post.cid)
        Vue.delete(state.postOrder, postIndex)
      }
    },
    UPDATED_POST_ORDER(state, postOrder: Cid[]): void {
      state.postOrder = postOrder
    },
    UPDATED_POST(state, post: Post): void {
      Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    UPDATED_POST_SUCCESS(): void {},
    UPDATED_POST_ERROR(state, post): void {
      // TODO: recover to old post
      Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    START_EDITING_POST(state, postCid): void {
      state.postBeingEditedCid = postCid
    },
    STOP_EDITING_POST(state): void {
      state.postBeingEditedCid = null
    },
    CHANGE_POST_TEXT(state, { postCid, attributes }: { postCid: Cid; attributes: PostAttributes }): void {
      let postToBeSaved: Post = state.postEntitiesByCid[postCid]
      postToBeSaved = Object.assign(postToBeSaved, attributes)
      Vue.set(state.postEntitiesByCid, postCid, postToBeSaved)
    },
    // Deprecated method that uses sortIndex
    UPDATE_POST_SORT_INDEX(state, { postCid, sortIndex }): void {
      if (sortIndex) {
        Vue.set(state.postEntitiesByCid[postCid], 'sort_index', sortIndex)
      }
    },
    UPDATE_POST_SECTION(state, { postCid, sectionId }): void {
      if (sectionId) {
        Vue.set(state.postEntitiesByCid[postCid], 'wall_section_id', sectionId)
      }
    },
    MOVED_POST(state, { postCid, position }): void {
      if (typeof position.left === 'number') {
        Vue.set(state.postEntitiesByCid[postCid], 'left', position.left)
      }
      if (typeof position.top === 'number') {
        Vue.set(state.postEntitiesByCid[postCid], 'top', position.top)
      }
    },
    RESIZED_POST(state, { postCid, size }): void {
      if (size && size.width) {
        Vue.set(state.postEntitiesByCid[postCid], 'width', size.width)
      }
    },
    APPROVE_POST(state, { postCid }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'published', true)
    },
    REMOVE_POST_COLOR(state, { postCid }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'color', null)
    },
    CHANGE_POST_COLOR(state, { postCid, color }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'color', color)
    },
    SAVE_ATTACHMENT_CHAT(state, attachment): void {
      if (!state.postBeingEditedCid) {
        state.chatAttachment = attachment
      } else {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', attachment)
      }
    },
    SAVE_CAPTION_AS_TITLE(state, title): void {
      if (state.postBeingEditedCid) {
        const post = state.postEntitiesByCid[state.postBeingEditedCid]
        // do not overwrite posts that already have subject, setting caption as title is low priority
        if (post.subject) return
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'subject', title)
      }
    },
    SAVE_ATTACHMENT(state, attachment): void {
      if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', attachment)
      }
    },
    REMOVE_ATTACHMENT(state, postCid): void {
      if (postCid) {
        Vue.set(state.postEntitiesByCid[postCid], 'attachment', null)
      } else if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', null)
      }
    },
    SAVE_FILE(state, file): void {
      if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'file', file)
      }
    },
    CHANGE_ATTACHMENT(state, { postCid, attributes }): void {
      Vue.set(state.postEntitiesByCid[postCid || attributes.cid], 'attachment', attributes.attachment)
    },
    SET_MOST_RECENTLY_CHANGED_GEOLOCATION_POST_CID(state, cid): void {
      state.mostRecentlyChangedGeolocationPostCid = cid
    },
  },
  actions: {
    /* ------------
     * CR-D
     * ------------ */
    async pingPosts({ rootGetters }): Promise<void> {
      // Only send the network request to update the cache, do nothing with the response
      try {
        await PadletApi.Wish.fetch({ wallId: rootGetters.wallId })
      } catch (e) {
        captureFetchException(e, { source: 'pingPosts' })
      }
    },
    // shouldResetState is a flag to signal that this fetch should reset the post list
    // only reset after we fetch the first page, follow up pages should only update the state
    async fetchPosts({ dispatch, rootGetters }, { shouldResetState }): Promise<any> {
      try {
        const { data, meta }: JsonAPIResponse<Wish> = await PadletApi.Wish.fetch({ wallId: rootGetters.wallId })
        dispatch('fetchPostsSuccess', { data, isFirstPage: true, isLastPage: !meta?.next, shouldResetState })
        // if the browser supports service worker, we will use stale-while-revalidate and serve the cached version of the first page here
        // after we get the fresh version from network request, next pages will be fetched based on that (see app/javascript/packs/surface.ts)
        // => we don't fetch next page indicated by the cached data here

        // The conditions navigator.serviceWorker && navigator.serviceWorker.controller && /serviceworker\.js|OneSignalSDKWorker\.js/.test(navigator.serviceWorker.controller.scriptURL)
        // is to handle the transition phase to the new service worker and can be safely removed later
        if (
          meta?.next &&
          (!navigator.serviceWorker || // browser does not support service worker
            !navigator.serviceWorker.controller || // service worker not installed
            // old service worker running
            (navigator.serviceWorker &&
              navigator.serviceWorker.controller &&
              /serviceworker\.js|OneSignalSDKWorker\.js/.test(navigator.serviceWorker.controller.scriptURL)))
        ) {
          dispatch('fetchPostsForPage', { pageStart: meta.next })
        }
      } catch (e) {
        captureFetchException(e, { source: 'fetchPosts' })
      }
    },
    fetchPostsForPage({ dispatch, rootGetters }, { pageStart }): void {
      PadletApi.Wish.fetch({ wallId: rootGetters.wallId, pageStart })
        .then((wishesPage: JsonAPIResponse<Wish>) => {
          const { data, meta } = wishesPage
          dispatch('fetchPostsSuccess', { data, isFirstPage: false, isLastPage: !meta!.next, shouldResetState: false })
          if (meta!.next) {
            dispatch('fetchPostsForPage', { pageStart: meta!.next })
          }
        })
        .catch((e) => {
          captureFetchException(e, { source: 'fetchPostsPage' })
        })
    },
    fetchPostsSuccess(context, { data, isFirstPage, isLastPage, shouldResetState }): void {
      const { commit, dispatch } = context
      const newPosts = data || []
      const newPostEntitiesByCid = reconcilePostsAfterFetch(context, newPosts, {
        isFirstPage,
        isLastPage,
        shouldResetState,
      })

      commit('FETCH_POSTS_SUCCESS', { newPostEntitiesByCid, isInitialPaginatedDataLoadDone: isLastPage })

      if (isLastPage) dispatch('postAction/recalibrate', null, { root: true })
    },
    async fetchPostOrder({ dispatch, getters, rootGetters }): Promise<void> {
      try {
        const postOrderInIds = await PadletApi.WishOrder.readAll({ wall: { id: rootGetters.wallId } })
        const getPostById = getters.getPostByServerId
        // Map Id to Cid
        const postOrder = postOrderInIds
          .map((id: Id) => {
            const post = getPostById(id) // Post can be null if it's a post that needs approval
            return post ? post.cid : null
          })
          .filter((cid: Cid | null) => !!cid)
        dispatch('fetchPostOrderSuccess', { postOrder })
      } catch (e) {
        captureFetchException(e, { source: 'fetchPostOrder' })
      }
    },
    fetchPostOrderSuccess({ commit }, { postOrder }): void {
      commit('FETCH_POST_ORDER_SUCCESS', postOrder)
    },
    createPendingPost({ commit }, { postAttributes }): void {
      commit('ADD_PENDING_POST', postAttributes)
    },
    /**
     * Start a new post (either by the pink button or double-clicking)
     * Similar to createPost, but createPost creates the post and submits it immediately
     * startNewPost will creates the post locally with empty content and set it as being
     * edited.
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    startNewPost(
      { rootState, rootGetters, state, commit, dispatch, getters },
      { attributes, wishOrder }: { attributes: PostAttributes; wishOrder?: { previousCid; nextCid } } = {
        attributes: { wall_section_id: undefined, file: null },
        wishOrder: undefined,
      },
    ): void {
      if (rootGetters.isUsingNewComposerModal && !!rootGetters['composerModalAlert/shouldShow']) {
        return
      }

      // the app has implemented a native new post button which calls "startNewPost" when tapped together with the new post modal
      // in map format, we should only open the native location picker when the button is tapped (same behavior as the add post button in surface_map_location_picker.vue)

      // if there is no pre-filled attribute, it means the action is triggered from the add post button
      const triggeredFromNewPostButton = !attributes || Object.keys(attributes).length === 0

      if (rootGetters.isApp && appCan('newPostModal') && rootGetters.isMap && triggeredFromNewPostButton) {
        dispatch('map/startPickingLocation', {}, { root: true })
        return
      }
      let newPostAttributes = { ...attributes }
      const usableSections = rootGetters['section/sections'].filter((x: Section): boolean => !isSectionNew(x))
      if (rootGetters.isShelf && !newPostAttributes.wall_section_id) {
        const assignedWallSectionId = usableSections
          .map((x: Section) => x.id)
          .includes(state.mostRecentlyTouchedSectionId)
          ? state.mostRecentlyTouchedSectionId
          : usableSections[0].id
        newPostAttributes.wall_section_id = assignedWallSectionId
      }
      dispatch('draft/setDraftNumber', { cid: getters.postBeingEditedCid }, { root: true })

      dispatch('removeEmptyPostBeingEdited')
      newPostAttributes = buildPost(newPostAttributes, { state: rootState, getters: rootGetters })
      if (rootGetters.isMap && !newPostAttributes.location_point) {
        dispatch('savePost', { attributes: newPostAttributes })
      } else {
        commit('START_NEW_POST', newPostAttributes)
        if (rootGetters.isUsingNewComposerModal) {
          dispatch('draft/updateActiveDraft', getters.postBeingEdited, { root: true })
        }
      }
      dispatch('putPostInPostOrderPosition', {
        postCid: state.postBeingEditedCid,
        previousCid: wishOrder ? wishOrder.previousCid : null,
        nextCid: wishOrder ? wishOrder.nextCid : null,
      })
    },
    /**
     * Create post immediately. Example scenarios: paste/drop to post and in composer mode
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    createPost(
      context: ActionContext<PostState, RootState>,
      { attributes } = { attributes: { wall_section_id: null, file: null } },
    ): void {
      // when a new post is to be created there are a few scenarios:
      // 1. We have all the data we need
      // 2. We have a pending file upload
      // 3. We have a pending section id
      // 4. Both 2 and 3

      // if the post is missing a section and there are more than one sections,
      // add post to the pending list
      // (scenario 3 or 4)
      const { commit, dispatch, rootState, rootGetters } = context

      let newPostAttributes = addCidIfAbsent(attributes)
      const usableSections = rootGetters['section/sections'].filter((x: Section): boolean => !isSectionNew(x))

      if (rootGetters.isShelf && !newPostAttributes.wall_section_id && usableSections.length !== 1) {
        commit('ADD_PENDING_POST', newPostAttributes)
      } else {
        // if there is only one section, use that section
        if (rootGetters.isShelf && !newPostAttributes.wall_section_id && usableSections.length === 1) {
          newPostAttributes.wall_section_id = usableSections[0].id
        }
        // if there is a pending file, add to pending
        // (scenario 2)
        if ('file' in newPostAttributes && newPostAttributes.file) {
          commit('ADD_PENDING_POST', newPostAttributes)

          // otherwise, create a new post
          // (scenario 1)
        } else {
          newPostAttributes = buildPost(newPostAttributes, {
            state: rootState,
            getters: rootGetters,
          })
          savePost(context, newPostAttributes)
          dispatch('markNewestUserCreatedPost', newPostAttributes.cid)
        }
      }
      dispatch('putPostInPostOrderPosition', {
        postCid: newPostAttributes.cid,
        previousCid: attributes.previousCid,
        nextCid: attributes.nextCid,
      })
    },
    markNewestUserCreatedPost({ commit, dispatch, getters, rootGetters }, postCid): void {
      commit('MARK_NEWEST_POST', postCid)
      const postWithPopupBeingOpenedCid = rootGetters['map/postWithPopupBeingOpenedCid']
      const isAPopupOpened = postWithPopupBeingOpenedCid && getters.isPostAlive(postWithPopupBeingOpenedCid)
      if (rootGetters.isMap && !isAPopupOpened) {
        dispatch('map/openPostPopup', { postCid: postCid }, { root: true })
      }
    },
    removeEmptyPostBeingEdited({ dispatch, getters, rootGetters }): void {
      // TODO: we won't need this once we completely switch to the new post modal
      if (!getters.postBeingEdited || !isEmpty(getters.postBeingEdited) || rootGetters.isUsingNewComposerModal) return
      dispatch('deletePost', { post: getters.postBeingEdited })
    },
    /**
     * Used in composer mode (post being edited in the composer instead inside the
     * post itself real-timely)
     * The action is used when user press save after finish editing
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    savePost(
      context: ActionContext<PostState, RootState>,
      { attributes }: { attributes: Post | PostAttributes },
    ): Post {
      const { state, getters, commit, dispatch, rootState, rootGetters } = context
      let postToBeSaved
      let existingPost: PostAttributes | null = null
      if (!attributes.cid || !attributes.id || !attributes.wall_id) {
        // if new post -> post will be created
        if (rootGetters.isUsingNewComposerModal && !attributes.id) {
          // new post added from the post modal should have its sort_index recalculated
          if (rootGetters.isTimeline) {
            attributes.sort_index = recomputeTimelineSortIndex(attributes.sort_index, getters.sortedPosts)
          } else {
            // set to undefined to be recalculated
            attributes.sort_index = undefined
          }
        }
        postToBeSaved = buildPost(attributes, { state: rootState, getters: rootGetters })
        savePost(context, postToBeSaved)
        dispatch('markNewestUserCreatedPost', postToBeSaved.cid)
      } else {
        existingPost = state.postEntitiesByCid[attributes.cid]
        // post is being edited ->  post will be updated
        postToBeSaved = attributes
        if (!rootGetters.canIPostWithoutWaitingForApproval) {
          // updating a post need to be re-approved
          postToBeSaved.published = false
        }
        savePost(context, postToBeSaved)
      }
      dispatch('putPostInPostOrderPosition', {
        postCid: postToBeSaved.cid,
        previousCid: null,
        nextCid: null,
      })
      dispatch('draft/removeDraft', postToBeSaved.cid, { root: true })
      dispatch('stopEditingPost')

      // in map format, we should pan to the new location if the post's location changed
      // For now just update mostRecentlyChangedGeolocationPostCid and there will be a watcher inside surface_map_google_marker to handle the panning
      if (rootGetters.isUsingNewComposerModal && rootGetters.isMap) {
        if (
          !existingPost ||
          !existingPost.location_point ||
          postToBeSaved.location_point.latitude !== existingPost.location_point.latitude ||
          postToBeSaved.location_point.longitude !== existingPost.location_point.longitude
        ) {
          commit('SET_MOST_RECENTLY_CHANGED_GEOLOCATION_POST_CID', postToBeSaved.cid)
        }
      }
      return postToBeSaved
    },
    savePostAttributes(context, postAttributes): void {
      savePost(context, postAttributes)
    },
    /* ------------
     * POST MODIFICATION OPERATIONS
     * ------------ */
    startEditingPost({ commit, dispatch, getters, rootGetters }, { postCid, shouldFocusAttachmentCaption }): void {
      if (isAppUsing('newPostEditModal') && !!rootGetters['composerModalAlert/shouldShow']) {
        return
      }
      dispatch('draft/setDraftNumber', { cid: getters.postBeingEditedCid }, { root: true })
      dispatch('stopEditingPost')
      commit('START_EDITING_POST', postCid)
      if (rootGetters.isUsingNewComposerModal && !rootGetters['draft/activeDraft']) {
        dispatch('draft/updateActiveDraft', getters.postBeingEdited, { root: true })
        if (shouldFocusAttachmentCaption) dispatch('composerModal/setShouldFocusAttachmentCaption', {}, { root: true })
      }
    },
    stopEditingPost({ commit, dispatch, state }): void {
      dispatch('removeEmptyPostBeingEdited')
      if (!state.postBeingEditedCid) return
      commit('STOP_EDITING_POST')
    },
    syncPostBeingEditedCidForMap({ dispatch, state, rootGetters }, { cid }: Post): void {
      // We need to do this so that on mobile, the postBeingEditedCid is updated
      // to the cid of the post that is just created and saved.
      // TODO: Make mobile map native, then remove this.
      if (rootGetters.isMap) {
        if (cid === state.postBeingEditedCid) {
          dispatch('startEditingPost', { postCid: cid })
        }
      }
    },
    resetMostRecentlyChangedGeolocationPostCid({ commit }): void {
      // in map format, we need to pan to a post whose location has just changed (handled by a watcher in surface_container_map.vue)
      // we need to reset mostRecentlyChangedGeolocationPostCid after panning so it can still pan when a post changes location twice in a row
      commit('SET_MOST_RECENTLY_CHANGED_GEOLOCATION_POST_CID', null)
    },
    changePostText(context, { postCid, attributes }): void {
      const { state, commit } = context
      commit('CHANGE_POST_TEXT', { postCid, attributes })
      const postToBeSaved = state.postEntitiesByCid[postCid]
      if (isEmpty(postToBeSaved)) {
        // Do nothing
        // We do not save empty post, but when the edit is over,
        // stopEditingPost will handle the empty post and delete it
      } else {
        savePost(context, postToBeSaved)
      }
    },
    completePostUpload(context, { file, status }): void {
      const { commit, dispatch, getters, rootState, rootGetters } = context
      let completedPostAttributes = getters.pendingPosts.find((post): boolean => post.file === file)
      commit('REMOVE_PENDING_POST', completedPostAttributes)
      if (status !== 'error' && status !== 'cancel') {
        completedPostAttributes = {
          ...completedPostAttributes,
          attachment: status,
        }
        completedPostAttributes = buildPost(completedPostAttributes, {
          state: rootState,
          getters: rootGetters,
        })
        dispatch('putPostInPostOrderPosition', {
          postCid: completedPostAttributes.cid,
          previousCid: null,
          nextCid: null,
        })
        savePost(context, completedPostAttributes)
        dispatch('markNewestUserCreatedPost', completedPostAttributes.cid)
      }
    },
    updateAfterPostCreated(
      context,
      { postCid, wishOrder }: { postCid: Cid; wishOrder?: { previous: Id; next: Id } },
    ): void {
      const post = context.state.postEntitiesByCid[postCid]
      if (!post) {
        return
      }
      /*
        One of the following state transitions will occur:
        - New >> Creating >> UpdateWaiting
        - Creating >> UpdateWaiting >> UpdateWaiting
        - UpdateWaiting >> UpdateWaiting >> UpdateWaiting
        - Updating >> UpdateWaiting >> UpdateWaiting
        - Created >> DebouncingUpdate >> DebouncingUpdate
        - DebouncingUpdate >> DebouncingUpdate >> DebouncingUpdate
      */
      savePost(context, { ...post, wish_order: wishOrder })
      savePost(context, { ...post, wish_order: wishOrder })
    },
    movedPost({ commit, dispatch }, { postCid, position }): void {
      commit('MOVED_POST', { postCid, position })
      dispatch('updateAfterPostCreated', { postCid })
    },
    updatePostOrder({ commit }, { postOrder }): void {
      commit('UPDATED_POST_ORDER', postOrder)
    },
    putPostInPostOrderPosition({ dispatch, rootGetters }, { postCid, previousCid, nextCid }): void {
      if (!isAppUsing('wishOrderArray')) return // TODO: Enable the flag
      if (previousCid || nextCid) {
        dispatch('sortedPost', {
          postCid: postCid,
          previousCid,
          nextCid,
        })
      } else if (rootGetters.newPostLocation === 'bottom') {
        dispatch('sortedPost', {
          postCid: postCid,
          to: 'back',
        })
      } else {
        dispatch('sortedPost', {
          postCid: postCid,
          to: 'front',
        })
      }
    },
    updatePostSection({ commit }, { postCid, sectionId }): void {
      commit('UPDATE_POST_SECTION', { sectionId, postCid })
    },
    updatePostSortIndex({ commit }, { postCid, sortIndex }): void {
      commit('UPDATE_POST_SORT_INDEX', { postCid, sortIndex })
    },
    sortedPost({ dispatch, getters }, { postCid, sortIndex, sectionId, previousCid, nextCid, to }): void {
      if (isAppUsing('wishOrderArray')) {
        if (previousCid || nextCid) {
          let newPostOrder = getters.postOrder.filter((cid) => cid !== postCid)
          if (nextCid) {
            // Put right before nextCid
            const position = newPostOrder.indexOf(nextCid)
            newPostOrder = [...newPostOrder.slice(0, position), postCid, ...newPostOrder.slice(position)]
          } else {
            // Put right after previousId
            const position = newPostOrder.indexOf(previousCid) + 1
            newPostOrder = [...newPostOrder.slice(0, position), postCid, ...newPostOrder.slice(position)]
          }
          dispatch('updatePostOrder', { postOrder: newPostOrder })
          dispatch('updatePostSection', { postCid, sectionId })
          const getPostByCid = getters.getPostByCid
          dispatch('updateAfterPostCreated', {
            postCid,
            wishOrder: {
              previous: previousCid ? getPostByCid(previousCid).id : null,
              next: nextCid ? getPostByCid(nextCid).id : null,
            },
          })
        } else if (to) {
          let newPostOrder = getters.postOrder.filter((cid: Cid): boolean => cid !== postCid)
          if (to === 'front') {
            // To the end of the array
            previousCid = newPostOrder[newPostOrder.length - 1]
            newPostOrder = newPostOrder.concat([postCid])
          } else if (to === 'back') {
            // To the front of the array
            nextCid = newPostOrder[0]
            newPostOrder = [postCid].concat(newPostOrder)
          }
          dispatch('updatePostOrder', { postOrder: newPostOrder })
          dispatch('updatePostSection', { postCid, sectionId })
          const getPostByCid = getters.getPostByCid
          dispatch('updateAfterPostCreated', {
            postCid,
            wishOrder: {
              previous: previousCid ? getPostByCid(previousCid).id : null,
              next: nextCid ? getPostByCid(nextCid).id : null,
            },
          })
        }
      }
      if (!isAppUsing('wishOrderArray')) {
        //TODO: Remove sortIndex
        dispatch('updatePostSortIndex', { postCid, sortIndex })
        dispatch('updatePostSection', { postCid, sectionId })
        dispatch('updateAfterPostCreated', { postCid })
      }
    },
    resizedPost({ commit, dispatch }, { postCid, size }): void {
      commit('RESIZED_POST', { postCid, size })
      dispatch('updateAfterPostCreated', { postCid })
    },

    // Post to delete may or may not be persisted yet.
    deletePost({ commit, getters, dispatch }, { post, postCid }): void {
      const { getPostByServerId: getById, getPostByCid: getByCid } = getters
      const postToDelete = post && post.id ? getById(post.id) : getByCid(postCid || (post && post.cid))
      deletePost({ commit, dispatch }, postToDelete)
      dispatch('draft/removeDraft', postToDelete.cid, { root: true })
      dispatch('removeConnectionsForPost', { postId: postToDelete.id }, { root: true })
    },
    approvePost({ state, commit }, { postCid }): void {
      commit('APPROVE_POST', { postCid })
      PadletApi.Wish.approve({ wishId: state.postEntitiesByCid[postCid].id })
    },
    removePostColor({ commit, dispatch }, { postCid }): void {
      commit('REMOVE_POST_COLOR', { postCid })
      dispatch('updateAfterPostCreated', { postCid })
    },
    changePostColor({ commit, dispatch }, { postCid, newBgColor }): void {
      commit('CHANGE_POST_COLOR', { postCid, color: newBgColor })
      dispatch('updateAfterPostCreated', { postCid })
    },
    /**
     * Does not trigger delete post on server with PadletApi
     */
    removePost({ commit, dispatch, getters, rootGetters }, { id, cid }): void {
      const post = id ? getters.getPostByServerId(id) : getters.getPostByCid(cid)
      if (!post) return
      if (rootGetters.isUsingNewComposerModal) {
        dispatch('draft/removeDraft', post.cid, { root: true })
      }
      commit('DELETED_POST', post)
    },
    addPost({ commit }, postAttributes): void {
      commit('CREATED_POST', postAttributes)
    },
    /* ------------
     * REMOTE
     * ------------ */
    newPostRemote({ dispatch, getters, rootGetters }, post): void {
      if (getters.canUserViewPost(post) || rootGetters.xUnpublishedPostPosition) {
        dispatch('addPost', post)
      }
    },
    editPostRemote({ dispatch, getters, rootGetters }, post: PostAttributes): void {
      const oldPost = getters.getPostByServerId(post.id)
      if (!oldPost) return

      const isFreshUpdate = oldPost.updated_at && post.updated_at && oldPost.updated_at < post.updated_at
      if (!isFreshUpdate) return

      if (getters.canUserViewPost(post) || rootGetters.xUnpublishedPostPosition) {
        dispatch('updatePostInStore', { ...oldPost, ...post })
      } else {
        dispatch('removePost', oldPost)
      }
    },
    editPostOrderRemote({ getters, dispatch }, postOrderInIds: Id[]): void {
      const getPostById = getters.getPostByServerId
      const postOrder: Cid[] = postOrderInIds
        .map((id: Id): Cid | null => {
          const post = getPostById(id)
          if (post) {
            return post.cid
          }
          return null
        })
        .filter((cid: Cid | null): boolean => {
          return cid !== null
        }) as Cid[]
      dispatch('updatePostOrder', { postOrder })
    },
    deletePostRemote({ dispatch }, post): void {
      dispatch('removePost', post)
    },
    clearAllPosts({ getters, commit, dispatch, rootGetters }): void {
      // Don't delete new posts being drafted
      deleteAllPosts(
        { commit, dispatch },
        getters.postsArray.filter((post) => !rootGetters.isUsingNewComposerModal || !isNew(post)),
      )
    },
    saveAttachmentCaption({ commit, rootGetters, dispatch, getters }, { caption }): void {
      if (rootGetters.xNewComposerModalPanel && !!getters.postBeingEditedCid) {
        // if the post modal is opened, save to the active draft
        dispatch('draft/updateActiveDraft', { attachment_caption: caption }, { root: true })
      } else {
        commit('SAVE_CAPTION_AS_TITLE', caption)
      }
    },
    saveContent(context, { content }): void {
      const { commit, dispatch, state, getters, rootGetters } = context
      dispatch('contentPicker/hideContentPickerPanel', null, { root: true })
      if (content.link) {
        if (rootGetters.format === 'chat') {
          commit('SAVE_ATTACHMENT_CHAT', content.link)
        } else if (rootGetters.xNewComposerModalPanel && !!getters.postBeingEditedCid) {
          // if the post modal is opened, save attachment to the current draft
          dispatch(
            'draft/updateActiveDraft',
            {
              attachment: content.link,
              wish_content: {
                attachment_props: {
                  url: content.link,
                },
                is_processed: false,
              },
            },
            { root: true },
          )
        } else {
          const post = state.postBeingEditedCid && state.postEntitiesByCid[state.postBeingEditedCid]
          if (!post) {
            dispatch('startNewPost', { attributes: { subject: '📹', attachment: content.link } })
          } else {
            commit('SAVE_ATTACHMENT', content.link)
            if (isEmpty(post)) {
              // do nothing
            } else {
              savePost(context, post)
            }
          }
        }
      } else {
        commit('SAVE_FILE', content.file)
      }
    },
    removePostAttachment({ state, commit, dispatch, rootGetters }, { postCid } = { postCid: null }): void {
      commit('REMOVE_ATTACHMENT', postCid)
      const post = state.postEntitiesByCid[postCid || state.postBeingEditedCid]
      if (post && isEmpty(post)) {
        // do nothing
      } else {
        dispatch('updateAfterPostCreated', { postCid: postCid || state.postBeingEditedCid })
      }
    },
    changePostAttachment({ commit, dispatch }, { postCid, attributes }): void {
      commit('CHANGE_ATTACHMENT', { postCid, attributes })
      dispatch('updateAfterPostCreated', { postCid })
    },
    pickSection(context, { sectionId }): void {
      const { getters, rootState, rootGetters, commit, dispatch } = context
      const postsToRemoveFromPending: any[] = []
      getters.pendingPosts.forEach((postAttributes): void => {
        // if a pending post has a wall_section_id, do nothing
        // it is likely waiting for something else (a file to complete uploading)
        if (!postAttributes.wall_section_id) {
          postAttributes.wall_section_id = sectionId
          // if the post has a pending file upload, do nothing
          let finalPostAttributes: PostAttributes = {}
          if (postAttributes.file) {
            // do nothing
            // if the post is blank, start a new post
          } else if (!postAttributes.subject && !postAttributes.body && !postAttributes.attachment) {
            finalPostAttributes = buildPost(postAttributes, { state: rootState, getters: rootGetters })
            dispatch('draft/setDraftNumber', { cid: getters.postBeingEditedCid }, { root: true })
            dispatch('stopEditingPost')
            commit('START_NEW_POST', finalPostAttributes)
            postsToRemoveFromPending.push(postAttributes)
            // otherwise, looks like the post has everything we need, create a new one
          } else {
            finalPostAttributes = buildPost(postAttributes, { state: rootState, getters: rootGetters })
            // for the new posting flow, we only create a new draft without saving the post
            if (rootGetters.isUsingNewComposerModal && !rootGetters.isApp) {
              // setting of shouldStartEditing should match what we use for handling objects dropped in surface-drag-and-drop-overlay.vue
              dispatch(
                'draft/startNewDraft',
                { attributes: finalPostAttributes, shouldStartEditing: !rootGetters['draft/isAnyDraftActive'] },
                { root: true },
              )
            } else {
              savePost(context, finalPostAttributes)
              dispatch('markNewestUserCreatedPost', finalPostAttributes.cid)
            }
            postsToRemoveFromPending.push(postAttributes)
          }
          if (finalPostAttributes.cid) {
            dispatch('putPostInPostOrderPosition', {
              postCid: finalPostAttributes.cid,
              previousCid: null,
              nextCid: null,
            })
          }
        }
      })
      // remove posts from pending list that were either added or created
      commit('REMOVE_PENDING_POSTS', { cids: postsToRemoveFromPending.map((post) => post.cid) })
    },
    cancelPickingSection({ rootGetters, commit }): void {
      // no section was picked, remove all pending posts that need a section
      if (rootGetters.isShelf) {
        commit('REMOVE_ALL_PENDING_POSTS_WITHOUT_SECTION')
      }
    },
    rejectPostsWithFileSizeExceeded({ commit }, file): void {
      commit('REJECT_POSTS_WITH_FILE_SIZE_EXCEEDED', file)
    },
    updatePostInStore({ commit, getters }, post: Post): void {
      if (post.cid) {
        commit('UPDATED_POST', post)
      } else {
        const postInStore = getters.getPostByServerId(post.id)
        if (!postInStore && getters.isPostMarkedAsDeleted(post)) return

        commit('UPDATED_POST', { ...post, cid: postInStore.cid })
      }
    },
    updatePendingPost({ commit }, postAttributes: PostAttributes): void {
      commit('UPDATE_PENDING_POST', postAttributes)
    },
    removePendingPost({ commit }, post: Post): void {
      commit('REMOVE_PENDING_POST', post)
    },
    removePendingPosts({ commit }, { cids }): void {
      commit('REMOVE_PENDING_POSTS', { cids })
    },
    createdPostSuccess(context, post: Post & UpdatePostArgs): void {
      const { commit, dispatch, getters } = context
      if (getters.isPostMarkedAsDeleted(post)) return
      if (post.wish_order) {
        commit('UPDATED_POST_ORDER', post.wish_order)
        // Post & UpdatePostArgs -> Post. We do not want wish_order to be saved
        delete post.wish_order
      }
      commit('SAVE_POST_ID', post)
      dispatch('syncPostBeingEditedCidForMap', post)
      dispatch('updatePostLatLongFromAttachment', post)
    },
    async updatePostLatLongFromAttachment(context, post: Post): Promise<void> {
      const { dispatch, rootGetters } = context
      if (!post || !post.attachment) return

      const data = await getDisplayAttributes(post.attachment)
      if (!data) return

      dispatch('attachment/updateLinkDisplayAttributes', { url: post.attachment, attributes: data }, { root: true })

      const latlong = data.metadata?.latlong
      if (!latlong) return

      savePost(context, {
        ...post,
        location_point: {
          longitude: latlong[1],
          latitude: latlong[0],
        },
      })
    },
    updatedPostSuccess({ commit }, post: Post): void {
      commit('UPDATED_POST_SUCCESS', post)
    },
    enqueueRequest(
      context,
      { post, customRequest }: { post: Post; customRequest: CustomSavePostRequest },
    ): Promise<any> {
      return customSavePost(context, post, customRequest)
    },
  },
}

export default postModule
export { PostState }
